import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionIntro } from '../alt/SectionIntro';
import { AgentBuilderCard, ApprovalCard, CategoryChip, ConnectionsCard, SharedAgentsCard, TemplateWall } from './agentArtefacts';
const CONNECT_IMAGE = 'https://cdn.magicpatterns.com/uploads/hJZmgqcQ1jKjsQ1YwRUstr/hf_20260906_211028_5de5584d-d238-4fb1-afb6-dba925712826.png';
const EASE = [0.16, 1, 0.3, 1] as const;
type PanelProps = {
  children: React.ReactNode;
  className?: string;
  tint?: 'white' | 'peach' | 'sand';
  delay?: number;
};
function Panel({
  children,
  className = '',
  tint = 'white',
  delay = 0
}: PanelProps) {
  const reduce = useReducedMotion();
  const surface = tint === 'peach' ? 'bg-[#FBF7F1]' : tint === 'sand' ? 'bg-[#F7F3ED]' : 'bg-white';
  return <motion.article initial={reduce ? false : {
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-90px'
  }} transition={{
    duration: 0.3,
    delay,
    ease: EASE
  }} className={`relative flex min-w-0 flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] ${surface} shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)] ${className}`} data-id="element-269" data-mp="1pqmdghtjgm6o">
      {children}
    </motion.article>;
}
type PanelCopyProps = {
  title: string;
  copy: string;
  className?: string;
  children?: React.ReactNode;
};
function PanelCopy({
  title,
  copy,
  className = '',
  children
}: PanelCopyProps) {
  return <div className={className} data-id="element-270" data-mp="yhqundpcjgb0">
      <h3 className="font-title text-[22px] font-bold leading-[1.14] tracking-[-0.025em] text-[#2B211A] md:text-[24px]" data-id="element-271" data-mp="yj1y1cevqm1l">
        {title}
        <span className="text-[#EA580C]" data-id="element-272" data-mp="orp7qp8qc3xo">.</span>
      </h3>
      <p className="mt-2.5 max-w-[42ch] text-[14px] leading-[1.65] text-[#6B5D52] md:text-[14.5px]" data-id="element-273" data-mp="os79u2i2pcdi">
        {copy}
      </p>
      {children}
    </div>;
}
function ApprovalVisual() {
  return <div className="relative flex h-[250px] min-h-0 items-center justify-center overflow-hidden rounded-[18px] bg-[#F3EEE7] px-5 md:min-h-[290px] md:px-7 xl:h-full xl:rounded-l-none xl:rounded-r-[21px]" data-id="element-274" data-mp="et00dqegm1vo">
      <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.12]" data-id="element-275" data-mp="2co5ez5s2hqy6" />
      <div className="relative w-full max-w-[300px]" data-id="element-276" data-mp="27qn77ipsdhrr">
        <ApprovalCard data-id="element-277" data-mp="22s23h9bbsfyk" />
      </div>
    </div>;
}
function ConnectionsVisual() {
  return <div className="relative h-[250px] overflow-hidden rounded-[18px] bg-[#EAE4DC] md:min-h-[300px] lg:h-full lg:rounded-l-none lg:rounded-r-[21px]" data-id="element-278" data-mp="30faufju3ih7y">
      <img src={CONNECT_IMAGE} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 size-full object-cover object-[18%_70%] opacity-40 saturate-[0.55]" data-id="element-279" data-mp="2vdv7tnc9s2ng" />
      <div aria-hidden="true" className="absolute inset-0 bg-[#F7F3ED]/35" data-id="element-280" data-mp="2mhmbwini3m62" />
      <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:w-[230px] md:bottom-6 md:left-6" data-id="element-281" data-mp="1f312g74to73b">
        <ConnectionsCard data-id="element-282" data-mp="1b27fbfz7ewbh" />
      </div>
    </div>;
}
export function AgentFeatureMosaic() {
  return <div data-id="element-283" data-mp="3mln0jhj3okja">
      <SectionIntro align="left" className="max-w-[720px]" data-id="element-284" data-mp="3qp1i4cmot8yu">
        <h2 className="font-title text-[34px] font-bold leading-[1.08] tracking-[-0.03em] text-[#2B211A] md:text-[52px]" data-id="element-285" data-mp="3qpkyvq0lprba">
          Built for the work that repeats<span className="text-[#EA580C]" data-id="element-286" data-mp="4a0azta9dxp7">.</span>
        </h2>
        <p className="mt-5 max-w-[54ch] text-[16.5px] leading-relaxed text-[#6B5D52] md:text-lg" data-id="element-287" data-mp="4bjtlqje0ku5">
          Move past one-off prompts. Hand a recurring responsibility to an agent that knows your
          company, reads the systems you already run on, and reports back where your team already
          works.
        </p>
      </SectionIntro>

      <div className="mt-12 grid grid-cols-1 gap-4 md:mt-16 lg:grid-cols-12 lg:gap-5" data-id="element-288" data-mp="n8unevm92m27">
        <Panel tint="peach" className="lg:col-span-7 lg:min-h-[340px]" data-id="element-289" data-mp="2k0uw49bgiaj0">
          <div className="grid h-full min-w-0 grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(240px,1.1fr)]" data-id="element-290" data-mp="2k2z9dhv8wlvy">
            <PanelCopy title="Create an agent in minutes" copy="Describe the role, the priorities and the outcomes it should care about — no code, no flowchart. Then choose where it runs, what it is allowed to reach, and which model sits behind it." className="p-6 sm:p-7 md:pr-5" data-id="element-291" data-mp="1tpj29lqh4v83">
              <div className="mt-6 flex flex-wrap gap-2" data-id="element-292" data-mp="1ypnlvkmoi62j">
                <CategoryChip category="marketing" data-id="element-293" data-mp="1az1e7ugl6nt2" />
                <CategoryChip category="sales" data-id="element-294" data-mp="1aztbratquvlz" />
                <CategoryChip category="people" data-id="element-295" data-mp="15zymfbpmae31" />
              </div>
            </PanelCopy>
            <div className="flex min-w-0 items-center justify-center px-5 pb-6 sm:px-6 xl:pb-0" data-id="element-296" data-mp="1koup0t8wmyw9">
              <div className="w-full max-w-[275px]" data-id="element-297" data-mp="1kqnuw6bp1f37">
                <AgentBuilderCard data-id="element-298" data-mp="1fxcp88imb4cv" />
              </div>
            </div>
          </div>
        </Panel>

        <Panel className="lg:col-span-5 lg:min-h-[340px]" delay={0.07} data-id="element-299" data-mp="s78tsofl2awy">
          <PanelCopy title="Start fast with pre-built templates" copy="Kick off with agents designed for real jobs across sales, marketing, support, finance and HR. Then change anything about them — the brief, the schedule, the tools — to match the way your team actually works." className="p-6 pb-0 sm:p-7 sm:pb-0" data-id="element-300" data-mp="na8r8nhxzqal" />
          {/* One horizontal shelf of four templates. The internal canvas is
           deliberately wide before being reduced, so moving from 2×2 to four
           columns does NOT make each card smaller. The shelf sits higher in
           its viewport and gets enough height for the card shadows and pointer
           to finish rather than being clipped at the panel's bottom edge. */}
          <div className="relative mx-auto mt-auto h-[132px] w-full overflow-hidden" data-id="element-301" data-mp="38oorzz6ehwv8">
            <div className="absolute -top-3 left-1/2 w-[900px] max-w-none origin-top -translate-x-1/2 scale-[0.46]" data-id="element-302" data-mp="3cme356sbb7lu">
              <TemplateWall data-id="element-303" data-mp="1x9ta84cgjfag" />
            </div>
          </div>
        </Panel>

        <Panel tint="sand" className="lg:col-span-12 lg:min-h-[300px]" data-id="element-304" data-mp="21ada3fof0x0a">
          <div className="grid h-full min-w-0 grid-cols-1 lg:grid-cols-[minmax(320px,0.92fr)_minmax(0,1.08fr)]" data-id="element-305" data-mp="7pa0fm1kaz9t">
            <PanelCopy title="Securely connect your work tools" copy="Generic AI doesn't know your business; a Giga agent does. Connections are made once, centrally, and every agent reaches your systems through them — never through somebody's personal login." className="p-6 sm:p-7 md:self-center md:p-9 lg:p-11" data-id="element-306" data-mp="2rqlejx1iigt" />
            <ConnectionsVisual data-id="element-307" data-mp="3g5v9t3y3vudg" />
          </div>
        </Panel>

        <Panel className="lg:col-span-7 lg:min-h-[330px]" data-id="element-308" data-mp="3g8hemvtm3vrz">
          <div className="grid h-full min-w-0 grid-cols-1 xl:grid-cols-[minmax(250px,0.9fr)_minmax(0,1.1fr)]" data-id="element-309" data-mp="3b7tjpz29iush">
            <PanelCopy title="Stay in control while agents work" copy="Before an agent sends a message, files a ticket or updates a record, it stops and shows you exactly what is about to happen. Approve it once, allow it always, or decline — every time, on every action." className="p-6 sm:p-7 md:self-center md:pr-5" data-id="element-310" data-mp="369ryp8la0led" />
            <ApprovalVisual data-id="element-311" data-mp="1gy4utgy838p7" />
          </div>
        </Panel>

        <Panel tint="peach" className="lg:col-span-5 lg:min-h-[330px]" delay={0.07} data-id="element-312" data-mp="1lq6c5ha8zpm3">
          <PanelCopy title="Share an agent with the whole team" copy="An agent built once belongs to the group, not to the person who made it. Access follows the group it sits in, so the right people get it on day one and nobody quietly rebuilds the same thing." className="p-6 pb-0 sm:p-7 sm:pb-0" data-id="element-313" data-mp="1ls91s07hngqa" />
          {/* The directory previously filled the card from edge to edge and
           competed with the claim. At half scale it reads as a product
           vignette, with enough quiet ground around it to see the stack. */}
          <div className="relative mx-auto mt-auto h-[155px] w-full overflow-hidden" data-id="element-314" data-mp="2eebyg8gy9mo1">
            <div className="absolute left-1/2 top-2 w-[360px] max-w-none origin-top -translate-x-1/2 scale-[0.46]" data-id="element-315" data-mp="2jc3oewy1aypl">
              <SharedAgentsCard data-id="element-316" data-mp="34hz554z33edw" />
            </div>
          </div>
        </Panel>
      </div>
    </div>;
}